import {
  Box,
  Flex,
  FormGroup,
  notification,
  Typography,
  useForm,
} from '@ergolabs/ui-kit';
import React, { FC, ReactNode, useState } from 'react';

import { whitelistSignup } from '../../../../api/whitelistSignup/whitelistSignup';
import { sigUsdAsset } from '../../../../common/assets/sigUsdAsset';
import { AssetControlFormItem } from '../../../../common/components/AssetControl/AssetControl';
import {
  OperationForm,
  OperationValidator,
} from '../../../../common/components/OperationForm/OperationForm';
import { AssetInfo } from '../../../../common/models/AssetInfo';
import { Currency } from '../../../../common/models/Currency';

export interface CommitmentFormProps {
  readonly roundName: string;
  readonly title: ReactNode | ReactNode[] | string;
  readonly address: string;
  readonly validators?: OperationValidator<CommitmentFormModel>[];
}

export interface CommitmentFormModel {
  readonly address: string;
  readonly asset: AssetInfo;
  readonly amount?: Currency;
}

export const CommitmentForm: FC<CommitmentFormProps> = ({
  roundName,
  address,
  title,
  validators = [],
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const form = useForm<CommitmentFormModel>({
    amount: undefined,
    asset: sigUsdAsset,
    address: address,
  });

  const amountRequiredValidator: OperationValidator<CommitmentFormModel> = (
    form,
  ) => (!form.value.amount?.isPositive() ? 'Enter an amount' : undefined);

  const normalizedValidators = [amountRequiredValidator, ...validators];

  const submit = ({
    value: { amount, address },
  }: FormGroup<CommitmentFormModel>) => {
    if (amount && address) {
      setSubmitting(true);
      whitelistSignup(roundName, address, amount).subscribe({
        next: () => {
          notification.success({ message: 'Your commitment was received' });
          setSubmitting(false);
        },
        error: () => {
          notification.error({
            message: 'Wallet already signed up for this event',
          });
          setSubmitting(false);
        },
      });
    }
  };

  return (
    <Box borderRadius="l" padding={4}>
      <OperationForm
        actionCaption={submitting ? 'Submitting' : 'Submit'}
        validators={normalizedValidators}
        form={form}
        submitting={submitting}
        onSubmit={submit}
      >
        <Flex col>
          <Flex.Item marginBottom={6}>{title}</Flex.Item>
          <Flex.Item marginBottom={2}>
            <Typography.Body>Commitment in SigUSD</Typography.Body>
          </Flex.Item>
          <Flex.Item>
            <AssetControlFormItem
              amountName="amount"
              tokenName="asset"
              readonly="asset"
            />
          </Flex.Item>
        </Flex>
      </OperationForm>
    </Box>
  );
};
